import clsx from 'clsx';
import classes from './Blank.module.scss';
import { useGameSounds } from '@educaplay/core';
import { COMPLETAR_SOUNDS } from '../../utils/constants';
import { useEffect, useImperativeHandle, useState } from 'react';
import { useAnimate, motion } from 'framer-motion';
import { useSelector } from 'react-redux';

export const InputBlank = (props) => {
    const { blankId, handleOnChange, onFocus, onBlur, gameState, setGameState, ref } = props;
    const gameSounds = useGameSounds();

    const isAnimating = useSelector(state => state.game.isAnimating);

    const [wordRef, animateWord] = useAnimate();    
    const [animationStatus, setAnimationStatus] = useState(null);

    useImperativeHandle(ref, () => ({
        success: async () => {
            wordRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            gameSounds.play(COMPLETAR_SOUNDS.CORRECT);
            setAnimationStatus('success');
            await animateWord(wordRef.current, { y: [-20, 0] }, {  duration: .3 });
        },
        fail: async () => {
            wordRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            gameSounds.play(COMPLETAR_SOUNDS.WRONG);
            setAnimationStatus('fail');
            await animateWord(wordRef.current, { y: [-20, 0] }, {  duration: .3 });
        },
        empty: async () => {
            wordRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setAnimationStatus(null);
            await animateWord(wordRef.current, { x: [-20, 18, -10, 4, 0] }, { duration: .3 });
        }
    }));
        
    useEffect(() => {
        setAnimationStatus(null);
    }, [gameState]);

    if (!gameState.textBlanks) return;

    const blank = gameState.textBlanks.find(blank => blank.id === blankId);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') event.preventDefault();
    }

    const handleFocus = (evt) => {
        if (evt.target.innerText !== '') {
            // Select all text
            const range = document.createRange();
            const selection = window.getSelection();
            range.selectNodeContents(evt.target);
            selection.removeAllRanges();
            selection.addRange(range);
        }
        
        const newGameState = {
            ...gameState,
            textBlanks: gameState.textBlanks.map(blank => {
                if (blank.id === blankId) {
                    return {
                        ...blank,
                        isCorrect: null
                    }
                }
                return blank;
            })
        }
        setGameState(newGameState);
        wordRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        onFocus();
    }

    const handlePaste = (event, id) => {
        event.preventDefault();
        const text = (event.clipboardData || window.clipboardData).getData('text/plain');
        const newGameState = {
            ...gameState,
            textBlanks: gameState.textBlanks.map(blank => {
                if (blank.id === id) {
                    return {
                        ...blank,
                        userAnswer: text
                    }
                }
                return blank;
            })
        }
        event.target.innerText = text;
        const range = document.createRange();
        const selection = window.getSelection();
        range.setStart(event.target, event.target.childNodes.length);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        setGameState(newGameState);
    }

    const isFilled = blank?.userAnswer || animationStatus != null;
    const isCorrect = blank?.isCorrect === true || animationStatus === 'success';
    const isWrong = blank?.isCorrect === false || animationStatus === 'fail';
    

    return (
        <motion.span
            ref={wordRef}
            onMouseEnter={() => gameSounds.play(COMPLETAR_SOUNDS.HOVER)}
            className={
                clsx(
                    classes.inputBlank,
                    isFilled && classes['inputBlank--filled'],
                    isCorrect && classes['inputBlank--correct'],
                    isWrong && classes['inputBlank--wrong']
                )}
        >
            <span
                contentEditable={!blank?.isCorrect && !isAnimating}
                autoCapitalize="off"
                autoCorrect="off"
                autoComplete="off"
                onFocus={handleFocus}
                onBlur={onBlur}
                onKeyDown={(event) => handleKeyDown(event)}
                onPaste={(event) => handlePaste(event, blankId)}
                onInput={(event) => handleOnChange(blankId, event.target.innerText)}
                className={classes.input}
            />
        </motion.span>
    );
}